import { useSearchParams } from "Navigation/useSearchParams";
import {
  SOCIAL_LIST_PAGE_PARAM,
  SOCIAL_LIST_PAGE_SIZE,
} from "Pages/People/constants";
import { useCallback, useEffect } from "react";

/**
 * The 1-based page of the active social list tab, synced with the URL. Only the
 * active tab's section is mounted and a tab switch replaces the query string, so
 * one shared parameter is unambiguous and paging resets on tab change. The blocked
 * list sits outside the tabs and pages via local state instead.
 */
export const useSocialListPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = Math.max(
    1,
    Number(searchParams.get(SOCIAL_LIST_PAGE_PARAM)) || 1,
  );

  const setPage = useCallback(
    (page: number) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (page > 1) {
          next.set(SOCIAL_LIST_PAGE_PARAM, String(page));
        } else {
          next.delete(SOCIAL_LIST_PAGE_PARAM);
        }
        return next;
      });
    },
    [setSearchParams],
  );

  return { currentPage, setPage };
};

/** The shape every paged social list shares, whatever its row type. */
type PagedList = {
  items: ReadonlyArray<unknown>;
  totalCount: number;
};

/**
 * Companion of a paged social list, called with the section's query result and
 * paging pair: when the current page points past the end of the list (a stale
 * link, or the last row of the last page was just removed), the page snaps back
 * to the last non-empty one. Data-driven sync, not a user-input reaction — the
 * server said this page is empty, so paging must move to a page that exists.
 */
export const useSnapBackToLastPage = (
  data: PagedList | undefined,
  currentPage: number,
  setPage: (page: number) => void,
) => {
  const isPastEnd =
    !!data && data.items.length === 0 && data.totalCount > 0 && currentPage > 1;
  const lastPage = data
    ? Math.max(1, Math.ceil(data.totalCount / SOCIAL_LIST_PAGE_SIZE))
    : 1;

  useEffect(() => {
    if (isPastEnd) {
      setPage(lastPage);
    }
  }, [isPastEnd, lastPage, setPage]);
};
